import { Localizer } from '../utils/localize.js';
import Button from '../wje-button/button.js';
import { default as WJElement, event } from '../wje-element/element.js';
import FormatDigital from '../wje-format-digital/format-digital.js';
import Icon from '../wje-icon/icon.js';
import Slider from '../wje-slider/slider.js';
import styles from './styles/styles.css?inline';

/**
 * @summary This element allows users to upload files.
 * `FileUploadItem` is a custom web component that represents a file upload item.
 * It extends from `WJElement` and uses the `Localizer` utility for localization.
 * @documentation https://elements.webjet.sk/components/file-upload
 * @status stable
 * @augments WJElement
 * @csspart button - The delete button part
 * @csspart image - The image part
 * @csspart name - The name part
 * @csspart size - The size part
 * @slot img - Slot for the image
 * @slot action - Slot for the action buttons
 * @cssproperty --primary-color - The primary color of the file upload item.
 * //@fires wje-button:click - Dispatches when the delete button is clicked
 * @tag wje-file-upload
 */
export default class FileUploadItem extends WJElement {
    /**
     * Creates an instance of FileUploadItem.
     * @class
     */
    constructor() {
        super();
        this.localizer = new Localizer(this);
    }

    /**
     * Sets the 'is-uploaded' attribute to indicate the uploaded status.
     * @param {boolean} value The value to determine if the element is uploaded.
     */
    set isUploaded(value) {
        this.setAttribute('is-uploaded', '');
    }

    /**
     * Checks if the 'is-uploaded' attribute is present on the element.
     * @returns {boolean} True if the 'is-uploaded' attribute exists, otherwise false.
     */
    get isUploaded() {
        return this.hasAttribute('is-uploaded');
    }

    /**
     * Sets the size attribute of the element.
     * @param {string | number} value The value to set for the size attribute.
     */
    set size(value) {
        this.setAttribute('size', value);
    }

    /**
     * Retrieves the value of the 'size' attribute.
     * @returns {string|null} The value of the 'size' attribute, or null if the attribute is not present.
     */
    get size() {
        return this.getAttribute('size');
    }

    /**
     * Dependencies for the component.
     * @type {object}
     */
    dependencies = {
        'wje-format-digital': FormatDigital,
        'wje-button': Button,
        'wje-slider': Slider,
        'wje-icon': Icon,
    };

    className = 'FileUploadItem';

    /**
     * Returns the CSS styles for the component.
     * @static
     * @returns {CSSStyleSheet}
     */
    static get cssStyleSheet() {
        return styles;
    }

    /**
     * Returns the list of attributes to observe for changes.
     * @static
     * @returns {Array<string>}
     */
    static get observedAttributes() {
        return ['uploaded', 'is-uploaded'];
    }

    /**
     * A lifecycle method that is called when one of the observed attributes of the custom element is added, removed, or changed.
     * This method is used to react to changes in the specified attributes.
     * @param {string} name The name of the attribute that was changed.
     * @param {string|null} oldValue The previous value of the attribute before the change, or null if the attribute was not previously set.
     * @param {string|null} newValue The new value of the attribute after the change, or null if the attribute has been removed.
     * @returns {void} This method does not return a value.
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (super.attributeChangedCallback) {
            super.attributeChangedCallback(name, oldValue, newValue);
        }

        if (name === 'uploaded' && oldValue !== newValue && this.uploadedEl) {
            this.uploadedEl.setAttribute('value', newValue);

            let progress = (+newValue / +this.size) * 100 || 0;

            this.progress =  Math.round(progress, 0);
        }
    }

    /**
     * Sets up the attributes for the component.
     */
    setupAttributes() {
        this.isShadowRoot = 'open';
        this.syncAria();
    }

    /**
     * Method to draw the component on the screen.
     * @returns {DocumentFragment} The fragment containing the component.
     */
    draw() {
        let fragment = document.createDocumentFragment();

        let native = document.createElement('div');
        native.classList.add('native-file-upload-item');

        let slot = document.createElement('slot');
        slot.setAttribute('name', 'img');

        let image = document.createElement('div');
        image.setAttribute('part', 'image');
        image.classList.add('image');

        let name = document.createElement('span');
        name.classList.add('name');
        name.innerText = this.name;

        let actions = document.createElement('slot');
        actions.classList.add('actions');
        actions.setAttribute('name', 'action');

        let button = document.createElement('wje-button');
        button.setAttribute('fill', 'link');
        button.setAttribute('size', 'small');
        button.innerHTML = `<wje-icon name="x" size="small"></wje-icon>`;

        let sizeWrapper = document.createElement('span');
        sizeWrapper.classList.add('size');

        let uploaded = document.createElement('wje-format-digital');
        uploaded.setAttribute('value', this.uploaded || 0);
        uploaded.innerHTML = `<span slot="start">${this.localizer.translate('wj.file.upload.uploaded')}</span>`;

        let size = document.createElement('wje-format-digital');
        size.setAttribute('value', this.size || 0);
        size.innerHTML = `<span slot="start">&nbsp;${this.localizer.translate('wj.file.upload.from')} </span>`;

        let size2 = document.createElement('wje-format-digital');
        size2.setAttribute('value', this.size || 0);

        let slider = document.createElement('wje-progress-bar');
        slider.classList.add('file-progress');
        slider.setAttribute('id', 'id-' + this.lastModified);
        slider.setAttribute('progress', this.progress);
        slider.setAttribute('color', 'success');

        image.appendChild(slot);
        actions.appendChild(button);

        sizeWrapper.appendChild(uploaded);
        sizeWrapper.appendChild(size);

        native.appendChild(image);
        native.appendChild(name);

        if (!this.isUploaded)
            native.appendChild(sizeWrapper);
        else
            native.appendChild(size2);

        native.appendChild(actions);

        if (!this.isUploaded)
            native.appendChild(slider);

        fragment.appendChild(native);

        this.button = button;
        this.uploadedEl = uploaded;
        this.sliderEl = slider;
        this.nameEl = name;

        return fragment;
    }

    /**
     * Called after the component has been drawn.
     */
    afterDraw() {
        this.syncAria();
        this.button.addEventListener('wje-button:click', this.onDelete);
    }

    /**
     * Sync ARIA attributes on host and actions.
     */
    syncAria() {
        if (!this.hasAttribute('role')) {
            this.setAriaState({ role: 'listitem' });
        }

        const nameText = (this.nameEl?.textContent || this.name || '').trim();
        if (nameText && !this.hasAttribute('aria-label')) {
            this.setAriaState({ label: nameText });
        }

        if (this.button && !this.button.hasAttribute('aria-label')) {
            const label = nameText ? `Remove ${nameText}` : 'Remove file';
            this.button.setAttribute('aria-label', label);
        }
    }

    /**
     * Handles the delete action.
     */
    onDelete = () => {
        event.dispatchCustomEvent(this, 'wje-file-upload-item:remove', this.data);

        this.remove();
    };
}
